// Sensitive-field scrubbing for observability events.
//
// Runs inside Sentry's `beforeSend` hook (and any other error-capture
// pipeline). Goal: no secret or Personal Information should ever leave
// the Vertu runtime via an error report.
//
// Strategy:
//   1. Known-secret key names → replace value with "[Filtered]".
//   2. Known-PII key names (email, phone, id_number) → "[Redacted]".
//   3. Values that *look* like secrets (JWTs, sk_..., pk_..., Bearer ..)
//      → "[Filtered]".
//
// The function walks any nested object/array up to a max depth so
// request bodies, user contexts, extra blobs, and breadcrumb data are
// all covered.

const SECRET_KEYS = new Set(
  [
    "password",
    "secret",
    "secret_key",
    "webhook_secret",
    "access_token",
    "refresh_token",
    "id_token",
    "api_key",
    "api_token",
    "auth_token",
    "token",
    "authorization",
    "x-api-key",
    "cookie",
    "set-cookie",
  ].map((s) => s.toLowerCase()),
);

const PII_KEYS = new Set(
  ["email", "phone", "mobile", "id_number", "passport_number", "address"].map(
    (s) => s.toLowerCase(),
  ),
);

// Banking details must be removed entirely, including RPC argument names and
// camelCase form fields. Do not retain the last four digits in telemetry.
const BANKING_KEYS = new Set([
  "accountholder",
  "accountholdername",
  "accountnumber",
  "bankaccountnumber",
  "bankname",
  "branchcode",
  "accounttype",
  "bankdetails",
  "bankingdetails",
  "bankreference",
  "proofpath",
  "verificationnote",
]);

const SECRET_VALUE_PATTERNS: RegExp[] = [
  /^sk_(?:test|live)_[A-Za-z0-9]{10,}/,
  /^pk_(?:test|live)_[A-Za-z0-9]{10,}/,
  /\bBearer\s+[A-Za-z0-9\-._~+/]+=*/,
  /^eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+$/, // JWT
];

const MAX_DEPTH = 6;
const MAX_STRING_PREVIEW = 2048;

export function scrubSensitive<T>(value: T): T {
  return walk(value, 0) as T;
}

function walk(value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH) return "[Truncated depth]";

  if (value == null) return value;
  if (typeof value === "string") return redactString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "bigint") return value.toString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value.map((v) => walk(v, depth + 1));
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      const lower = key.toLowerCase();
      const field = lower.replace(/^p_/, "").replace(/[_-]/g, "");
      if (BANKING_KEYS.has(field)) {
        out[key] = "[Redacted]";
      } else if (SECRET_KEYS.has(lower) || lower === "p_token") {
        out[key] = "[Filtered]";
      } else if (PII_KEYS.has(lower)) {
        out[key] = typeof v === "string" ? redactPII(v) : "[Redacted]";
      } else {
        out[key] = walk(v, depth + 1);
      }
    }
    return out;
  }

  return "[Unknown]";
}

function redactString(s: string): string {
  // The path segment is a bearer credential, including when a URL appears in
  // a breadcrumb, transaction name, referrer, or free-form error message.
  s = s.replace(/(\/refund-details\/)[^/?#\s"'<>\\]+/gi, "$1[Filtered]");
  // A signed proof URL grants temporary access to private banking evidence.
  // Remove the entire object path/query even inside breadcrumbs or messages.
  s = s.replace(
    /(\/storage\/v1\/object\/sign\/refund-eft-proofs\/)[^\s"'<>\\]+/gi,
    "$1[Filtered]",
  );
  if (s.length > MAX_STRING_PREVIEW) {
    s = s.slice(0, MAX_STRING_PREVIEW) + "… (truncated)";
  }
  for (const pattern of SECRET_VALUE_PATTERNS) {
    if (pattern.test(s)) return "[Filtered]";
  }
  return s;
}

// Masks emails and phone numbers but leaves enough context to debug
// ("j***@example.com" rather than a flat "[Redacted]"). Useful when a
// customer reports an error and we need to confirm we see *their*
// event — without logging the full address.
function redactPII(s: string): string {
  if (s.includes("@")) {
    const [local, domain] = s.split("@");
    if (local && domain) {
      const masked =
        local.length <= 2
          ? "*".repeat(local.length)
          : local[0] + "*".repeat(Math.min(local.length - 1, 4));
      return `${masked}@${domain}`;
    }
  }
  if (/^\+?\d[\d\s\-()]{5,}$/.test(s)) {
    const digits = s.replace(/\D/g, "");
    const last4 = digits.slice(-4);
    return `***${last4}`;
  }
  return "[Redacted]";
}
